import {
  SubprinciplesConfigurationForm,
  SubprinciplesConfigurationFormValues,
} from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/edit/SubprinciplesConfigurationForm'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { CreateButton } from 'src/shared/button/Buttons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Option } from 'src/shared/form/control'
import {
  fetchPrincipleConfigs,
  insertSubprincipleMutation,
  mapFetchedPrincipleConfigsToOptions,
} from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/edit/SubprinciplesConfigFormUtils'
import { useUserLocale } from 'src/user/UserContext'
import { Subprinciple_Config_Insert_Input } from 'src/@types/graphql'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'

const defaultInitialValues: SubprinciplesConfigurationFormValues = {
  parentPrinciple: null,
  enName: '',
  deName: '',
  frName: '',
  itName: '',
  status: CONFIGURATION_STATUS.ACTIVE,
  enHelpInstructions: '',
  deHelpInstructions: '',
  frHelpInstructions: '',
  itHelpInstructions: '',
  helpAndInstructionsKey: '',
}

const onCreate = async () => {
  document.getElementById('subprinciples-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const PrinciplesConfigurationSubprinciplesCreatePage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  const [initialValues, setInitialValues] = useState<SubprinciplesConfigurationFormValues | null>(null)
  const [parentPrincipleOptions, setParentPrincipleOptions] = useState<Option[]>([])

  useEffect(() => {
    setInitialValues(defaultInitialValues)
  }, [])

  const initParentPrincipleOptions = useCallback(async () => {
    const { fetchedPrincipleConfigs } = await fetchPrincipleConfigs(urqlClient)()
    const mappedParentPrincipleOptions = mapFetchedPrincipleConfigsToOptions(
      fetchedPrincipleConfigs,
      getMessage,
      locale,
    )

    return {
      parentPrincipleOptions: mappedParentPrincipleOptions,
    }
  }, [getMessage, locale, urqlClient])

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.PrinciplesConfigurationIndex.nested.PrinciplesConfigurationSubprinciples.params({}))
  }, [navigate])

  const handleSubmitLocal = useCallback(
    async (values: SubprinciplesConfigurationFormValues, setFormDirty: Dispatch<boolean>) => {
      if (values.parentPrinciple == null) {
        notificationService.operationFailed()
        return
      }

      const subprincipleInsertInput: Subprinciple_Config_Insert_Input = {
        code: crypto.randomUUID(),
        names: {
          de: values.deName ?? '',
          en: values.enName ?? '',
          fr: values.frName ?? '',
          it: values.itName ?? '',
        },
        descriptions: {
          de: values.deHelpInstructions ? HtmlSanitizer.sanitize(values.deHelpInstructions) : '',
          en: values.enHelpInstructions ? HtmlSanitizer.sanitize(values.enHelpInstructions) : '',
          fr: values.frHelpInstructions ? HtmlSanitizer.sanitize(values.frHelpInstructions) : '',
          it: values.itHelpInstructions ? HtmlSanitizer.sanitize(values.itHelpInstructions) : '',
        },
        active: values.status === CONFIGURATION_STATUS.ACTIVE,
        principle_config_id: values.parentPrinciple,
      }

      try {
        await insertSubprincipleMutation(urqlClient)(subprincipleInsertInput)
        notificationService.changesSaved()
        setFormDirty(false)
        onBackHandler()
      } catch (e) {
        notificationService.operationFailed()
      }
    },
    [notificationService, onBackHandler, urqlClient],
  )

  useEffect(() => {
    initParentPrincipleOptions()
      .then((res) => {
        setParentPrincipleOptions(res.parentPrincipleOptions)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [initParentPrincipleOptions, notificationService])

  const formFieldOptions = {
    statusFieldOptions,
    parentPrincipleOptions,
  }

  return (
    <ScreenLayout
      title={getMessage('label.configuration.subprinciples.create')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={<CreateButton origin="header" onClick={onCreate} />}
    >
      <PageLayout>
        <>
          {initialValues && (
            <SubprinciplesConfigurationForm
              mode="CREATE"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
              formFieldOptions={formFieldOptions}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
